import {
	Camera,
	Check,
	FileText,
	LayoutGrid,
	ListChecks,
	Mail,
	Sheet,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { ExampleTag, shotProps } from "@/components/blocks/frames/Frames";
import { DURATION, EASE, SPRING_SOFT } from "@/components/motion/motion";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

/** Assembled (state 0, server-rendered) then scattered across the desk. */
const ASSEMBLE_DURATIONS = [5600, 2600] as const;

type Piece = "request" | "planning" | "survey" | "actions" | "report";

/** Where each piece lies when scattered, as a share of its own size. */
const SCATTER: Record<Piece, { x: string; y: string; rotate: number }> = {
	request: { x: "-16%", y: "-34%", rotate: -7 },
	planning: { x: "18%", y: "-30%", rotate: 6 },
	survey: { x: "-22%", y: "22%", rotate: 8 },
	actions: { x: "8%", y: "34%", rotate: -5 },
	report: { x: "26%", y: "14%", rotate: -9 },
};

const SOURCE_ICON: Record<Piece, React.ElementType> = {
	request: Mail,
	planning: Sheet,
	survey: Camera,
	actions: ListChecks,
	report: FileText,
};

const initials = (name: string) =>
	name
		.split(/\s+/)
		.map((word) => word[0])
		.join("")
		.slice(0, 2);

/**
 * Hero scene: the bits of a process scattered across emails, spreadsheets,
 * photos and notes snap into one business application, and back.
 */
export function AssembleScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const scene = dict.scenes.assemble;
	const { ref, index } = useSceneLoop(ASSEMBLE_DURATIONS);
	const assembled = index === 0;

	const sourceKey: Record<Piece, keyof typeof scene.sources> = {
		request: "email",
		planning: "sheet",
		survey: "photo",
		actions: "note",
		report: "pdf",
	};

	const piece = (key: Piece, order: number, children: React.ReactNode) => {
		const Icon = SOURCE_ICON[key];
		const scatter = SCATTER[key];
		return (
			<motion.div
				className="paper flex min-w-0 flex-col rounded-xl border p-3 shadow-sm"
				style={{ gridArea: key }}
				animate={
					assembled
						? { x: 0, y: 0, rotate: 0, scale: 1 }
						: { ...scatter, scale: 0.94 }
				}
				transition={{ ...SPRING_SOFT, delay: order * 0.06 }}
			>
				<div className="mb-2 flex items-center justify-between gap-2">
					<span className="relative flex h-5 min-w-0 items-center gap-1.5 overflow-hidden text-[10px] font-semibold">
						<Icon
							className={cn(
								"size-3.5 shrink-0 transition-colors duration-500",
								assembled ? "text-signal-ink" : "text-muted-foreground",
							)}
						/>
						<AnimatePresence mode="wait" initial={false}>
							<motion.span
								key={assembled ? "module" : "source"}
								className={cn(
									"truncate",
									!assembled && "font-mono font-medium tracking-wide uppercase",
								)}
								initial={{ opacity: 0, y: 6 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -6 }}
								transition={{ duration: DURATION.state, ease: EASE }}
							>
								{assembled ? scene.modules[key] : scene.sources[sourceKey[key]]}
							</motion.span>
						</AnimatePresence>
					</span>
				</div>
				{children}
			</motion.div>
		);
	};

	return (
		<div
			ref={ref}
			{...shotProps}
			className={cn("relative select-none", className)}
		>
			{/* The application window that appears around the pieces. */}
			<motion.div
				className="paper shot-shadow absolute inset-0 rounded-2xl border"
				initial={false}
				animate={{ opacity: assembled ? 1 : 0, scale: assembled ? 1 : 0.97 }}
				transition={{ duration: 0.6, ease: EASE, delay: assembled ? 0.25 : 0 }}
			>
				<div className="flex h-9 items-center gap-3 border-b bg-muted/60 px-3">
					<div className="flex gap-1.5">
						<span className="size-2.5 rounded-full bg-foreground/15" />
						<span className="size-2.5 rounded-full bg-foreground/15" />
						<span className="size-2.5 rounded-full bg-foreground/15" />
					</div>
					<span className="truncate text-[11px] font-semibold">
						{scene.appName}
					</span>
					<ExampleTag className="ml-auto">
						{dict.common.exampleBadge}
					</ExampleTag>
				</div>
				<div className="absolute top-9 bottom-0 left-0 hidden w-11 flex-col items-center gap-3 border-r bg-muted/40 pt-4 sm:flex">
					<span className="flex size-6 items-center justify-center rounded-md bg-foreground text-background">
						<LayoutGrid className="size-3.5" />
					</span>
					{[0, 1, 2].map((dot) => (
						<span
							key={dot}
							className="size-6 rounded-md bg-foreground/[0.07]"
						/>
					))}
				</div>
			</motion.div>

			<div
				className={cn(
					"relative grid h-full gap-2.5 p-3 pt-12 pb-9 sm:gap-3 sm:pb-3 sm:pl-[3.6rem]",
					"[grid-template-areas:'request_request'_'survey_actions'_'report_report'] grid-cols-2 grid-rows-[auto_auto_auto]",
					"sm:[grid-template-areas:'request_request_planning'_'survey_actions_report'] sm:grid-cols-[1.1fr_1fr_1fr] sm:grid-rows-[auto_1fr]",
				)}
			>
				{piece(
					"request",
					0,
					<div className="space-y-1.5">
						<p className="truncate text-[12px] font-semibold">
							{scene.request.subject}
						</p>
						<p className="flex items-center gap-2 text-[10px] text-muted-foreground">
							<span className="flex size-4 items-center justify-center rounded-full bg-foreground/10 text-[8px] font-semibold text-foreground">
								{initials(scene.request.from)}
							</span>
							{scene.request.from}
							<span className="ml-auto font-mono">{scene.request.time}</span>
						</p>
						<div className="space-y-1 pt-1">
							<span className="block h-1.5 w-full rounded-full bg-foreground/[0.07]" />
							<span className="block h-1.5 w-4/5 rounded-full bg-foreground/[0.07]" />
						</div>
					</div>,
				)}
				<div className="hidden sm:contents">
					{piece(
						"planning",
						1,
						<ul className="space-y-1.5 text-[10px]">
							{Object.entries(scene.planning).map(([key, row], rowIndex) => (
								<li
									key={key}
									className="flex items-center gap-2 border-b pb-1.5 last:border-0 last:pb-0"
								>
									<span
										className={cn(
											"size-1.5 rounded-full",
											rowIndex === 0 ? "bg-signal" : "bg-foreground/20",
										)}
									/>
									<span className="flex-1 truncate font-medium">{row.who}</span>
									<span className="font-mono text-muted-foreground">
										{row.when}
									</span>
								</li>
							))}
						</ul>,
					)}
				</div>
				{piece(
					"survey",
					2,
					<div className="flex flex-1 flex-col gap-2">
						<div className="dot-grid relative min-h-14 flex-1 overflow-hidden rounded-lg bg-muted">
							<svg
								viewBox="0 0 100 50"
								preserveAspectRatio="none"
								className="absolute inset-x-0 bottom-0 h-3/5 w-full"
								aria-hidden="true"
							>
								<path
									d="M0 50 L0 34 L22 18 L40 32 L62 10 L100 38 L100 50 Z"
									className="fill-foreground/15"
								/>
								<path
									d="M0 50 L0 42 L30 30 L55 40 L80 26 L100 36 L100 50 Z"
									className="fill-foreground/25"
								/>
							</svg>
							<span className="absolute top-2 right-2 size-3 rounded-full bg-signal/70" />
						</div>
						<p className="truncate text-[11px] font-semibold">
							{scene.survey.label}
						</p>
						<p className="truncate font-mono text-[9px] text-muted-foreground">
							{scene.survey.meta}
						</p>
					</div>,
				)}
				{piece(
					"actions",
					3,
					<ul className="space-y-2 text-[10.5px]">
						{Object.entries(scene.actions).map(([key, action], actionIndex) => {
							const done = assembled && actionIndex < 2;
							return (
								<li key={key} className="flex items-start gap-2">
									<span
										className={cn(
											"mt-px flex size-3.5 shrink-0 items-center justify-center rounded border transition-colors duration-300",
											done && "border-signal bg-signal text-signal-foreground",
										)}
										style={{
											transitionDelay: done
												? `${0.9 + actionIndex * 0.5}s`
												: "0s",
										}}
									>
										<Check className="size-2.5" strokeWidth={3} />
									</span>
									<span className="leading-tight text-pretty">{action}</span>
								</li>
							);
						})}
					</ul>,
				)}
				{piece(
					"report",
					4,
					<div className="flex flex-1 flex-col justify-between gap-2">
						<div className="space-y-2">
							<p className="text-[12px] font-semibold">{scene.report.title}</p>
							<div className="hidden space-y-1 sm:block">
								{["w-full", "w-5/6", "w-full", "w-2/3"].map((width, line) => (
									<span
										key={`${width}-${line}`}
										className={cn(
											"block h-1 rounded-full bg-foreground/[0.08]",
											width,
										)}
									/>
								))}
							</div>
						</div>
						<div className="space-y-1.5">
							<div className="h-1.5 overflow-hidden rounded-full bg-foreground/[0.07]">
								<motion.div
									className="h-full origin-left rounded-full bg-signal"
									initial={false}
									animate={{ scaleX: assembled ? 1 : 0.15 }}
									transition={{
										duration: assembled ? 1.4 : 0.4,
										ease: EASE,
										delay: assembled ? 1.2 : 0,
									}}
								/>
							</div>
							<p className="flex items-center gap-1.5 text-[10px] font-medium">
								<span className="size-1.5 rounded-full bg-signal" />
								{scene.report.status}
								<span className="ml-auto font-mono text-muted-foreground">
									{scene.sources.pdf}
								</span>
							</p>
						</div>
					</div>,
				)}
			</div>

			<motion.div
				className="dark absolute -bottom-4 left-6 flex items-center gap-2 rounded-full border bg-background px-3.5 py-2 text-xs font-medium text-foreground shadow-lg sm:left-16"
				initial={false}
				animate={{ opacity: assembled ? 1 : 0, y: assembled ? 0 : 8 }}
				transition={{ duration: 0.5, ease: EASE, delay: assembled ? 1.6 : 0 }}
			>
				<span className="flex size-4 items-center justify-center rounded-full bg-signal text-signal-foreground">
					<Check className="size-2.5" strokeWidth={3} />
				</span>
				{scene.status}
			</motion.div>
		</div>
	);
}
